/**
 * 006-handle-gifting: ClaimAuthorization payload encoding + Ed25519 sigverify
 * instruction builder (FR-021).
 *
 * Mirrors the Rust `ClaimAuthorizationPayload` struct in
 * `contracts/programs/morfi-opinion/src/helpers/ed25519_payload.rs`. The byte
 * layout MUST match exactly; the on-chain handler reconstructs the same bytes
 * from its account context and asserts equality with the Ed25519-signed
 * message extracted from the sigverify preamble.
 *
 * Layout (140 bytes total):
 *   domain[16] | vault[32] | claimant[32] | destination[32] | nonce[16]
 *   | expiry_unix_le[8] | expected_deposit_count_le[4]
 *
 * `expected_deposit_count` is a snapshot of `HandleVault.deposit_count`
 * captured by the backend at the moment it signs the authorization. The
 * on-chain handler asserts the vault's current `deposit_count` matches
 * this value — any new gift deposited between auth issuance and claim
 * execution invalidates the auth, preventing the recipient from sealing
 * the vault and stranding the new gift inside it.
 */

import { Ed25519Program, type PublicKey, type TransactionInstruction } from '@solana/web3.js';

/**
 * Domain separator. Exactly 16 bytes; matches Rust `CLAIM_AUTHORIZATION_DOMAIN`.
 *
 * Trailing two zero bytes are intentional — `b"morfi-claim-v1"` is 14 bytes
 * and the array is padded to a fixed 16-byte width for borsh encoding.
 */
export const CLAIM_AUTHORIZATION_DOMAIN: Uint8Array = (() => {
  const out = new Uint8Array(16);
  const tag = new TextEncoder().encode('morfi-claim-v1');
  out.set(tag, 0);
  // bytes 14, 15 stay zero
  return out;
})();

/** Total serialized payload length. */
export const CLAIM_AUTHORIZATION_PAYLOAD_BYTES = 140;

/** Nonce is exactly 16 bytes. */
export const CLAIM_AUTHORIZATION_NONCE_BYTES = 16;

/** Maximum value of the `expected_deposit_count` u32 field. */
export const CLAIM_AUTHORIZATION_MAX_DEPOSIT_COUNT = 0xff_ff_ff_ff;

/** Inputs to {@link encodeClaimAuthorizationPayload}. */
export interface ClaimAuthorizationPayloadInput {
  vaultPubkey: PublicKey;
  claimantPubkey: PublicKey;
  destinationWallet: PublicKey;
  /** 16-byte random nonce (must be exactly 16 bytes). */
  nonce: Uint8Array;
  /** Unix timestamp (seconds) after which the authorization is invalid. */
  expiryUnix: bigint | number;
  /**
   * Snapshot of `HandleVault.deposit_count` at the moment the backend
   * signed this authorization. Encoded as a little-endian `u32`.
   *
   * The on-chain handler asserts the vault's current `deposit_count`
   * equals this value; any new gift deposited between issuance and
   * execution will fail the claim with
   * `ClaimAuthorizationStaleDepositCount` (error code 6520).
   */
  expectedDepositCount: number;
}

/**
 * Encode a `ClaimAuthorizationPayload` to its borsh byte representation.
 *
 * The output bytes are exactly what the backend must Ed25519-sign and pass into
 * {@link buildEd25519VerifyInstruction}. The on-chain handler reconstructs the
 * same bytes and asserts byte-equality.
 *
 * @throws Error if `nonce.length !== 16`.
 */
export function encodeClaimAuthorizationPayload(input: ClaimAuthorizationPayloadInput): Uint8Array {
  if (input.nonce.length !== CLAIM_AUTHORIZATION_NONCE_BYTES) {
    throw new Error(
      `encodeClaimAuthorizationPayload: nonce must be exactly ${CLAIM_AUTHORIZATION_NONCE_BYTES} bytes, got ${input.nonce.length}`,
    );
  }
  if (
    !Number.isInteger(input.expectedDepositCount) ||
    input.expectedDepositCount < 0 ||
    input.expectedDepositCount > CLAIM_AUTHORIZATION_MAX_DEPOSIT_COUNT
  ) {
    throw new Error(
      `encodeClaimAuthorizationPayload: expectedDepositCount must be an integer in [0, ${CLAIM_AUTHORIZATION_MAX_DEPOSIT_COUNT}], got ${input.expectedDepositCount}`,
    );
  }

  const out = new Uint8Array(CLAIM_AUTHORIZATION_PAYLOAD_BYTES);
  let offset = 0;

  // domain[16]
  out.set(CLAIM_AUTHORIZATION_DOMAIN, offset);
  offset += 16;

  // vault_pubkey[32]
  out.set(input.vaultPubkey.toBytes(), offset);
  offset += 32;

  // claimant_pubkey[32]
  out.set(input.claimantPubkey.toBytes(), offset);
  offset += 32;

  // destination_wallet[32]
  out.set(input.destinationWallet.toBytes(), offset);
  offset += 32;

  // nonce[16]
  out.set(input.nonce, offset);
  offset += 16;

  // expiry_unix: i64 little-endian
  // Use DataView to write a signed 64-bit integer.
  const expiryBig =
    typeof input.expiryUnix === 'bigint' ? input.expiryUnix : BigInt(Math.trunc(input.expiryUnix));
  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  view.setBigInt64(offset, expiryBig, /* littleEndian */ true);
  offset += 8;

  // expected_deposit_count: u32 little-endian
  view.setUint32(offset, input.expectedDepositCount, /* littleEndian */ true);

  return out;
}

/** Inputs for {@link buildEd25519VerifyInstruction}. */
export interface BuildEd25519VerifyInput {
  /** 32-byte Ed25519 public key of the escrow claim authority. */
  escrowAuthorityPublicKey: Uint8Array;
  /** Borsh-encoded ClaimAuthorizationPayload (output of {@link encodeClaimAuthorizationPayload}). */
  message: Uint8Array;
  /** 64-byte Ed25519 signature over `message`. */
  signature: Uint8Array;
}

/**
 * Build an Ed25519Program sigverify preamble instruction. This MUST be the
 * instruction immediately preceding `claim_handle_vault` in the same
 * transaction (FR-021). The on-chain handler reads the previous instruction
 * via the `instructions` sysvar and asserts:
 *   1. program_id == Ed25519Program::ID
 *   2. signer pubkey == platform_config.escrow_claim_authority
 *   3. signed message == reconstructed ClaimAuthorizationPayload bytes
 *
 * Uses {@link Ed25519Program.createInstructionWithPublicKey} which constructs
 * the well-known instruction layout the on-chain Ed25519 native program
 * expects.
 *
 * @throws Error if pubkey is not 32 bytes or signature is not 64 bytes.
 */
export function buildEd25519VerifyInstruction(
  input: BuildEd25519VerifyInput,
): TransactionInstruction {
  if (input.escrowAuthorityPublicKey.length !== 32) {
    throw new Error(
      `buildEd25519VerifyInstruction: publicKey must be 32 bytes, got ${input.escrowAuthorityPublicKey.length}`,
    );
  }
  if (input.signature.length !== 64) {
    throw new Error(
      `buildEd25519VerifyInstruction: signature must be 64 bytes, got ${input.signature.length}`,
    );
  }

  return Ed25519Program.createInstructionWithPublicKey({
    publicKey: input.escrowAuthorityPublicKey,
    message: input.message,
    signature: input.signature,
  });
}
